import { useEffect, useState } from "react";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import FileUpload from "@/components/FileUpload";

// section, name, label, type
const FIELDS = [
  // BASICS
  { section: "Basics", name: "site_name", label: "Site Name" },
  { section: "Basics", name: "tagline", label: "Tagline" },
  { section: "Basics", name: "logo_url", label: "Logo", type: "image" },
  { section: "Basics", name: "primary_color", label: "Primary Color" },
  { section: "Basics", name: "secondary_color", label: "Secondary Color (CTA)" },
  { section: "Basics", name: "accent_color", label: "Accent Color" },

  // HERO
  { section: "Home — Hero", name: "hero_brand_name", label: "Bold Brand Name (huge saffron)" },
  { section: "Home — Hero", name: "hero_badge_text", label: "Hero Badge Text (top tag)" },
  { section: "Home — Hero", name: "hero_title_line1", label: "Hero Headline — Line 1 (white)" },
  { section: "Home — Hero", name: "hero_title_line2", label: "Hero Headline — Line 2 (muted)" },
  { section: "Home — Hero", name: "hero_subtitle", label: "Hero Subtitle", type: "textarea" },
  { section: "Home — Hero", name: "hero_slides", label: "Hero Carousel Images", type: "imagelist" },
  { section: "Home — Hero", name: "stat_1_number", label: "Stat 1 — Number (e.g. 500+, 1000+)" },
  { section: "Home — Hero", name: "stat_1_label", label: "Stat 1 — Label (e.g. MACHINES DELIVERED)" },
  { section: "Home — Hero", name: "stat_2_number", label: "Stat 2 — Number" },
  { section: "Home — Hero", name: "stat_2_label", label: "Stat 2 — Label" },
  { section: "Home — Hero", name: "stat_3_number", label: "Stat 3 — Number" },
  { section: "Home — Hero", name: "stat_3_label", label: "Stat 3 — Label" },

  // ABOUT PAGE — Hero & Story Photos
  { section: "About Page — Hero & Photos", name: "about_hero_image", label: "About Page — Hero Background Photo", type: "image", help: "Big background photo shown behind the 'About' page title at the top." },
  { section: "About Page — Hero & Photos", name: "about_story_image", label: "About Page — 'Our Story' Section Photo", type: "image", help: "Photo shown beside the 'A One-Stop Solution' story text." },

  // ENGINEERS PAGE — Hero & Band Photos
  { section: "Engineers Page — Hero & Photos", name: "engineers_hero_image", label: "Engineers Page — Hero Background Photo", type: "image", help: "Big background photo behind the Engineers page title." },
  { section: "Engineers Page — Hero & Photos", name: "engineers_image", label: "Engineers Page — 'About the Vertical' Photo", type: "image", help: "Photo shown beside the engineers narrative content." },

  // PRODUCTS PAGE — Hero & Catalog
  { section: "Products Page — Hero & Catalog", name: "products_hero_image", label: "Products Page — Hero Background Photo", type: "image" },
  { section: "Products Page — Hero & Catalog", name: "product_catalog_url", label: "Product Catalog (PDF) — appears as a download button below the Products page hero text", type: "file", help: "Upload your complete product catalog PDF. Customers will see a 'Download Product Catalog' button right under the page title." },

  // SPARES PAGE — Hero & Catalog
  { section: "Spares Page — Hero & Catalog", name: "spares_hero_image", label: "Spares Page — Hero Background Photo", type: "image" },
  { section: "Spares Page — Hero & Catalog", name: "spares_catalog_url", label: "Spares Catalog (PDF) — appears as a download button below the Spares page hero text", type: "file", help: "Upload your spares catalog PDF. Customers will see a 'Download Spares Catalog' button right under the page title." },

  // MARQUEE
  { section: "Home — Marquee", name: "marquee_items", label: "Scrolling Marquee Items (one per line)", type: "stringlist" },

  // ABOUT BAND
  { section: "Home — About Band", name: "about_eyebrow", label: "Eyebrow Label" },
  { section: "Home — About Band", name: "about_title", label: "Title", type: "textarea" },
  { section: "Home — About Band", name: "about_subtitle", label: "Subtitle", type: "textarea" },
  { section: "Home — About Band", name: "about_badge", label: "Yellow Badge Text (next to image)" },
  { section: "Home — About Band", name: "about_badge_show", label: "Show the Yellow 'Authorised Dealer' Badge card", type: "boolean", help: "Toggle the yellow badge in the bottom-left corner of the About-Band image." },
  { section: "Home — About Band", name: "about_founded_show", label: "Show the Black 'Year Founded' Badge card", type: "boolean", help: "Toggle the small black card in the top-right corner of the About-Band image." },
  { section: "Home — About Band", name: "about_founded_year", label: "Year Founded — Big Number on Black Card", help: "e.g. 2016, 2010 — the giant orange number." },
  { section: "Home — About Band", name: "about_founded_label", label: "Year Founded — Small Label", help: "e.g. YEAR FOUNDED, ESTABLISHED, SINCE" },
  { section: "Home — About Band", name: "about_image", label: "About Band Image (separate from hero)", type: "image" },
  { section: "Home — About Band", name: "about_band_bullets", label: "Bullet Points (4 checks beside image)", type: "stringlist", help: "One per line. Change company names like 'Bull Machines / JCB / Volvo' to whatever you want." },

  // HOME — WHAT WE DO (services bento)
  { section: "Home — What We Do", name: "home_services_eyebrow", label: "Eyebrow" },
  { section: "Home — What We Do", name: "home_services_title", label: "Title" },
  { section: "Home — What We Do", name: "home_services_subtitle", label: "Subtitle", type: "textarea" },
  { section: "Home — What We Do", name: "home_services_items", label: "Service Cards (4 dark cards on home)", type: "objectlist", fields: [
    { key: "title", label: "Title" },
    { key: "desc", label: "Description" },
  ]},

  // CORPORATE VIDEO
  { section: "Home — Corporate Video", name: "corporate_video_eyebrow", label: "Eyebrow Label" },
  { section: "Home — Corporate Video", name: "corporate_video_title", label: "Section Title" },
  { section: "Home — Corporate Video", name: "corporate_video_subtitle", label: "Section Subtitle", type: "textarea" },
  { section: "Home — Corporate Video", name: "corporate_video_url", label: "Video — Upload MP4 OR paste YouTube/Vimeo URL", type: "videofile", help: "Leave EMPTY to hide this section. Either upload an MP4 from your computer, OR paste a YouTube/Vimeo link." },
  { section: "Home — Corporate Video", name: "corporate_profile_url", label: "Corporate Profile (PDF) — adds a 'Download Corporate Profile' button under the video", type: "file", help: "Upload your full company profile / pitch deck. Visitors will see a download button right below the corporate video." },

  // TESTIMONIALS
  { section: "Home — Testimonials", name: "testimonials_eyebrow", label: "Eyebrow Label" },
  { section: "Home — Testimonials", name: "testimonials_title", label: "Section Title" },

  // TEAM (About page)
  { section: "About — Team Section", name: "team_eyebrow", label: "Eyebrow Label" },
  { section: "About — Team Section", name: "team_title", label: "Section Title" },
  { section: "About — Team Section", name: "team_subtitle", label: "Section Subtitle", type: "textarea" },

  // ABOUT PAGE — Our Story
  { section: "About — Our Story", name: "about_story_image", label: "Our Story Section — Photo", type: "image" },
  { section: "About — Our Story", name: "about_story_eyebrow", label: "Eyebrow" },
  { section: "About — Our Story", name: "about_story_title", label: "Title" },
  { section: "About — Our Story", name: "about_story_text", label: "Story Text (rich text — use the toolbar)", type: "richtext" },
  // ABOUT — TIMELINE
  { section: "About — Timeline", name: "about_timeline_eyebrow", label: "Eyebrow" },
  { section: "About — Timeline", name: "about_timeline_title", label: "Title" },
  { section: "About — Timeline", name: "about_timeline_items", label: "Milestones (vertical animated timeline)", type: "objectlist", fields: [
    { key: "year", label: "Year (e.g. 2016, 2024)" },
    { key: "title", label: "Milestone Title" },
    { key: "desc", label: "Short Description" },
  ]},
  { section: "About — Tara Edge & MVK", name: "about_edge_eyebrow", label: "Tara Edge — Eyebrow" },
  { section: "About — Tara Edge & MVK", name: "about_edge_title", label: "Tara Edge — Title" },
  { section: "About — Tara Edge & MVK", name: "about_edge_bullets", label: "Tara Edge — Bullet Points", type: "stringlist", help: "One per line. Replace any company names you don't deal with." },
  { section: "About — Tara Edge & MVK", name: "about_mvk_items", label: "Mission / Vision / Key Market Cards", type: "objectlist", fields: [
    { key: "title", label: "Card Title" },
    { key: "desc", label: "Description" },
  ]},

  // CHATBOT (built-in rule-based)
  { section: "Built-in Chatbot", name: "chatbot_button_label", label: "Chat Header Title (default: Tara Assist)" },
  { section: "Built-in Chatbot", name: "chatbot_welcome", label: "Welcome Message (first thing user sees)", type: "textarea" },
  { section: "Built-in Chatbot", name: "chatbot_options", label: "Question Options (each becomes a button)", type: "objectlist", help: "Add as many or as few options as you want. Each one shows as a button to the visitor.", fields: [
    { key: "label", label: "Button Label (what the visitor clicks)" },
    { key: "reply", label: "Bot's Reply After Clicking" },
  ]},
  { section: "Built-in Chatbot", name: "chatbot_form_prompt", label: "Prompt Before Asking for Name+Phone", type: "textarea" },
  { section: "Built-in Chatbot", name: "chatbot_thankyou", label: "Thank-You Message After Lead Submitted", type: "textarea" },

  // WHATSAPP FAB
  { section: "WhatsApp Click-to-Chat", name: "whatsapp_enabled", label: "Show floating WhatsApp button on every page", type: "boolean" },
  { section: "WhatsApp Click-to-Chat", name: "whatsapp_number", label: "WhatsApp Number (with country code, NO + or spaces)", help: "Example: 917091639163 (NOT +91 70 9163 9163). Leave EMPTY to hide the button." },
  { section: "WhatsApp Click-to-Chat", name: "whatsapp_message", label: "Pre-filled Message (what auto-appears in WhatsApp)", type: "textarea" },

  // LEAD NOTIFICATION WEBHOOK
  { section: "Lead Notification Webhook", name: "lead_webhook_url", label: "Webhook URL (Zapier / Pabbly / Make / IFTTT / custom)", help: "Every new contact form, chatbot lead, and spare order will POST a JSON payload here. Leave EMPTY to disable. Use this to auto-forward leads to your WhatsApp, email, Slack, Telegram, Google Sheet etc via Zapier." },
  { section: "Lead Notification Webhook", name: "lead_webhook_secret", label: "Webhook Secret (optional)", help: "If set, sent as 'X-Webhook-Secret' header so your endpoint can verify the request is from you." },

  // APPLICATIONS
  { section: "Home — Applications", name: "applications_eyebrow", label: "Eyebrow" },
  { section: "Home — Applications", name: "applications_title", label: "Title", type: "textarea" },
  { section: "Home — Applications", name: "applications_subtitle", label: "Subtitle", type: "textarea" },
  { section: "Home — Applications", name: "applications_items", label: "Application Items (one per line)", type: "stringlist" },

  // DID YOU KNOW
  { section: "Home — Did You Know", name: "didyouknow_eyebrow", label: "Eyebrow" },
  { section: "Home — Did You Know", name: "didyouknow_text", label: "Stat / Quote", type: "textarea" },

  // FINAL CTA
  { section: "Home — Final CTA", name: "final_cta_title", label: "Title" },
  { section: "Home — Final CTA", name: "final_cta_subtitle", label: "Subtitle", type: "textarea" },
  { section: "Home — Final CTA", name: "final_cta_button", label: "Button Text" },

  // ENGINEERS PAGE
  { section: "Engineers Page", name: "engineers_eyebrow", label: "Eyebrow" },
  { section: "Engineers Page", name: "engineers_title", label: "Title", type: "textarea" },
  { section: "Engineers Page", name: "engineers_subtitle", label: "Subtitle", type: "textarea" },
  { section: "Engineers Page", name: "engineers_image", label: '"About the Vertical" Section Image', type: "image" },
  { section: "Engineers Page", name: "engineers_about", label: "About Text", type: "textarea" },
  { section: "Engineers Page", name: "engineers_services", label: "Service Cards (the 6 vertical 'Zero Downtime' band)", type: "objectlist", fields: [
    { key: "title", label: "Card Title" },
    { key: "desc", label: "Card Description" },
  ]},

  // SERVICES PAGE
  { section: "Services Page", name: "services_eyebrow", label: "Eyebrow" },
  { section: "Services Page", name: "services_hero_title", label: "Hero Title (last word auto-highlights saffron)", type: "textarea" },
  { section: "Services Page", name: "services_hero_subtitle", label: "Hero Subtitle", type: "textarea" },
  { section: "Services Page", name: "services_items", label: "Service Cards", type: "objectlist", fields: [
    { key: "title", label: "Service Title (e.g. BUY, SELL, FINANCE)" },
    { key: "desc", label: "Description" },
    { key: "points", label: "Bullet Points (comma-separated)" },
  ]},
  { section: "Services Page", name: "services_cta_title", label: "Yellow CTA Band — Title" },
  { section: "Services Page", name: "services_cta_button", label: "Yellow CTA Band — Button Text" },

  // CONTACT
  { section: "Contact", name: "phone", label: "Phone" },
  { section: "Contact", name: "email", label: "Email" },
  { section: "Contact", name: "address", label: "Registered Office Address", type: "textarea" },
  { section: "Contact", name: "head_office", label: "Head Office Address", type: "textarea" },
  { section: "Contact", name: "zonal_office", label: "Zonal Office Address", type: "textarea" },
  { section: "Contact", name: "map_embed_url", label: "Google Maps Embed URL", type: "textarea" },

  // LEGAL
  { section: "Legal", name: "cin", label: "CIN" },
  { section: "Legal", name: "gst", label: "GST" },

  // SOCIAL
  { section: "Social", name: "facebook", label: "Facebook URL" },
  { section: "Social", name: "instagram", label: "Instagram URL" },
  { section: "Social", name: "linkedin", label: "LinkedIn URL" },
  { section: "Social", name: "youtube", label: "YouTube URL" },
  { section: "Social", name: "twitter", label: "Twitter / X URL" },

  // FOOTER
  { section: "Footer", name: "footer_about_text", label: "Footer — Text below the logo (left column)", type: "textarea", help: "This is the paragraph that appears under the Tara Autohub logo in the footer." },
  { section: "Footer", name: "footer_office_label_1", label: "Footer — Label for Head Office address", help: "Default: 'Head Office'." },
  { section: "Footer", name: "footer_office_label_2", label: "Footer — Label for Zonal Office address", help: "Default: 'Zonal Office'." },
  { section: "Footer", name: "footer_extra_addresses", label: "Footer — Additional Office Addresses (add/remove)", type: "objectlist", help: "Add any number of extra offices — branch, warehouse, service centre. Each appears in the footer 'India' column.", fields: [
    { key: "label", label: "Address Label (e.g. Service Centre, Branch Office)" },
    { key: "address", label: "Full Address", type: "textarea" },
  ]},
  { section: "Footer", name: "footer_text", label: "Footer — Bottom Copyright Strip" },

  // SEO & SHARE
  { section: "SEO & Social Share", name: "seo_tagline", label: "Browser Tab Tagline (after Site Name in <title>)", help: "Example: 'Partnering Trust' → tab shows 'Tara Autohub — Partnering Trust'." },
  { section: "SEO & Social Share", name: "seo_title", label: "Custom <title> Override (optional)", help: "Leave EMPTY to auto-use 'Site Name — Tagline'. Fill only if you want a completely custom browser tab title." },
  { section: "SEO & Social Share", name: "seo_description", label: "Meta Description (160 chars, Google search snippet)", type: "textarea", help: "Used by Google in search results AND shared on WhatsApp/FB if no separate share description." },
  { section: "SEO & Social Share", name: "seo_keywords", label: "Meta Keywords / Hashtags (comma-separated)", type: "textarea", help: "Example: backhoe loader bihar, bull machines dealer, JCB resale patna, construction equipment finance. Use hashtags/long-tail phrases — improves discoverability." },
  { section: "SEO & Social Share", name: "seo_og_title", label: "Social Share Title (WhatsApp/Facebook/LinkedIn)", help: "Leave EMPTY to reuse browser tab title." },
  { section: "SEO & Social Share", name: "seo_og_description", label: "Social Share Description", type: "textarea", help: "Shown when someone shares your link on WhatsApp/FB/LinkedIn. Leave empty to reuse Meta Description." },
  { section: "SEO & Social Share", name: "seo_og_image", label: "Social Share Image (1200×630 PNG/JPG)", type: "image", help: "This image appears as the preview when your link is shared." },
  { section: "SEO & Social Share", name: "seo_canonical_url", label: "Canonical URL (your live domain)", help: "Example: https://taraautohub.in — tells Google this is the official URL." },

  // ANALYTICS & TRACKING
  { section: "Analytics & Tracking", name: "analytics_ga_id", label: "Google Analytics 4 — Measurement ID", help: "Get this from analytics.google.com → Admin → Data Streams. Looks like G-XXXXXXXXXX. Leave empty to disable." },
  { section: "Analytics & Tracking", name: "analytics_gtm_id", label: "Google Tag Manager — Container ID (optional)", help: "Looks like GTM-XXXXXXX. Only fill if you use GTM instead of/alongside GA4." },
  { section: "Analytics & Tracking", name: "analytics_meta_pixel_id", label: "Meta (Facebook) Pixel ID", help: "Get this from business.facebook.com → Events Manager. A numeric ID like 1234567890123456. Leave empty to disable." },
  { section: "Analytics & Tracking", name: "analytics_custom_head", label: "Custom <head> HTML (advanced — Hotjar, Clarity, etc.)", type: "textarea", help: "Paste any <script> or <meta> tags here. They will be injected into <head> on every page. Use carefully." },

  // BROCHURE GATING
  { section: "Brochure Lead Capture", name: "brochure_gating_enabled", label: "Capture lead BEFORE allowing brochure PDF download", type: "boolean", help: "When ON, clicking 'Download Brochure' on a product page opens a name+phone form first. The PDF only opens after submission. Turn OFF for direct download." },
  { section: "Brochure Lead Capture", name: "brochure_gating_title", label: "Gating Dialog Title" },
  { section: "Brochure Lead Capture", name: "brochure_gating_subtitle", label: "Gating Dialog Subtitle", type: "textarea" },
];

function ImageList({ value, onChange }) {
  const list = Array.isArray(value) ? value : [];
  const setAt = (i, v) => { const next = [...list]; next[i] = v; onChange(next); };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, ""]);
  return (
    <div className="space-y-3">
      {list.length === 0 && <div className="text-xs text-gray-500 italic">No images yet. The hero will fall back to a default photo.</div>}
      {list.map((url, i) => (
        <div key={i} className="flex gap-3 items-start" data-testid={`imagelist-row-${i}`}>
          <div className="flex-1"><FileUpload value={url} onChange={(v) => setAt(i, v)} kind="image" accept="image/*" /></div>
          <button type="button" onClick={() => remove(i)} className="p-2 text-red-600 hover:bg-red-50" aria-label="Remove image"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs uppercase font-bold tracking-widest text-[#E65C00] hover:underline inline-flex items-center gap-1" data-testid="imagelist-add">
        <Plus className="w-3 h-3" /> Add Image
      </button>
    </div>
  );
}

function StringList({ value, onChange }) {
  const text = Array.isArray(value) ? value.join("\n") : "";
  return (
    <textarea
      rows={6}
      className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm font-mono"
      defaultValue={text}
      onBlur={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
    />
  );
}

/** Hybrid: paste a URL OR upload a video file. */
function VideoFile({ value, onChange }) {
  return (
    <div className="space-y-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-1">Option 1 — Paste YouTube / Vimeo / direct URL</div>
        <input
          type="text"
          className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm"
          placeholder="https://www.youtube.com/watch?v=... or full https://...mp4 URL"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid="videofile-url"
        />
      </div>
      <div className="text-center text-xs text-gray-400 uppercase tracking-widest">— OR —</div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-1">Option 2 — Upload MP4 / MOV / WebM from your computer</div>
        <FileUpload value={value} onChange={onChange} accept="video/*,.mp4,.mov,.webm" kind="video" />
      </div>
      {value && (
        <button type="button" onClick={() => onChange("")} className="text-xs text-red-600 hover:underline">Clear video</button>
      )}
    </div>
  );
}

/** Repeatable list of objects (e.g. service cards, engineer service rows). */
function ObjectList({ value, onChange, fields }) {
  const list = Array.isArray(value) ? value : [];
  const setAt = (i, patch) => {
    const next = [...list]; next[i] = { ...next[i], ...patch }; onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => {
    const blank = {}; fields.forEach((f) => blank[f.key] = "");
    onChange([...list, blank]);
  };
  const moveUp = (i) => { if (i === 0) return; const next = [...list]; [next[i-1], next[i]] = [next[i], next[i-1]]; onChange(next); };
  const moveDown = (i) => { if (i === list.length - 1) return; const next = [...list]; [next[i+1], next[i]] = [next[i], next[i+1]]; onChange(next); };
  return (
    <div className="space-y-4 mt-2">
      {list.length === 0 && <div className="text-xs text-gray-500 italic">No items yet — click "Add" to create one.</div>}
      {list.map((row, i) => (
        <div key={i} className="border border-gray-300 bg-gray-50 p-4 relative">
          <div className="absolute top-2 right-2 flex gap-1">
            <button type="button" onClick={() => moveUp(i)} className="px-1.5 text-xs border border-gray-300 bg-white hover:bg-gray-100" title="Move up">↑</button>
            <button type="button" onClick={() => moveDown(i)} className="px-1.5 text-xs border border-gray-300 bg-white hover:bg-gray-100" title="Move down">↓</button>
            <button type="button" onClick={() => remove(i)} className="px-1.5 text-xs border border-red-300 text-red-600 bg-white hover:bg-red-50" title="Delete">×</button>
          </div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Item #{i + 1}</div>
          <div className="space-y-3">
            {fields.map((f) => (
              <div key={f.key}>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{f.label}</label>
                {f.key === "desc" || f.type === "textarea" ? (
                  <textarea
                    rows={2}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm bg-white"
                    value={row[f.key] || ""}
                    onChange={(e) => setAt(i, { [f.key]: e.target.value })}
                  />
                ) : (
                  <input
                    type="text"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm bg-white"
                    value={row[f.key] || ""}
                    onChange={(e) => setAt(i, { [f.key]: e.target.value })}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs uppercase font-bold tracking-widest text-[#E65C00] hover:underline inline-flex items-center gap-1">
        <Plus className="w-3 h-3" /> Add Item
      </button>
    </div>
  );
}

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
      refresh();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="skeleton h-96" />;

  // group by section preserving FIELDS order
  const sections = {};
  FIELDS.forEach((f) => { (sections[f.section] = sections[f.section] || []).push(f); });
  const sectionNames = Object.keys(sections);
  const current = activeSection && sections[activeSection] ? activeSection : sectionNames[0];

  const set = (k, v) => setForm({ ...form, [k]: v });

  const renderField = (f) => {
    const v = form[f.name];
    if (f.type === "boolean")
      return (
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!v}
            onChange={(e) => set(f.name, e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#E65C00]"
            data-testid={`settings-${f.name}`}
          />
          <div>
            <div className="text-sm font-bold text-gray-800">{f.label}</div>
            {f.help && <div className="text-xs text-gray-500 mt-1">{f.help}</div>}
          </div>
        </label>
      );
    if (f.type === "image")
      return <FileUpload value={v} onChange={(x) => set(f.name, x)} label={f.label} accept="image/*" kind="image" />;
    if (f.type === "file")
      return (
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{f.label}</label>
          {f.help && <div className="text-xs text-gray-500 mt-1">{f.help}</div>}
          <div className="mt-2">
            <FileUpload value={v} onChange={(x) => set(f.name, x)} accept={f.accept || ".pdf"} kind="file" />
          </div>
        </div>
      );
    if (f.type === "imagelist")
      return (
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{f.label}</label>
          <div className="mt-2"><ImageList value={v} onChange={(x) => set(f.name, x)} /></div>
        </div>
      );
    if (f.type === "stringlist")
      return (
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{f.label}</label>
          <StringList value={v} onChange={(x) => set(f.name, x)} />
        </div>
      );
    if (f.type === "videofile")
      return (
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{f.label}</label>
          {f.help && <div className="text-xs text-gray-500 mt-1">{f.help}</div>}
          <div className="mt-2"><VideoFile value={v} onChange={(x) => set(f.name, x)} /></div>
        </div>
      );
    if (f.type === "objectlist")
      return (
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{f.label}</label>
          <ObjectList value={v} onChange={(x) => set(f.name, x)} fields={f.fields || []} />
        </div>
      );
    if (f.type === "textarea")
      return (
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{f.label}</label>
          {f.help && <div className="text-xs text-gray-500 mt-1">{f.help}</div>}
          <textarea rows={3} className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={v || ""} onChange={(e) => set(f.name, e.target.value)} data-testid={`settings-${f.name}`} />
        </div>
      );
    return (
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{f.label}</label>
        {f.help && <div className="text-xs text-gray-500 mt-1">{f.help}</div>}
        <input className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={v || ""} onChange={(e) => set(f.name, e.target.value)} data-testid={`settings-${f.name}`} />
      </div>
    );
  };

  return (
    <div data-testid="admin-settings">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="heading-xl text-3xl">SITE SETTINGS</h1>
          <div className="text-xs uppercase tracking-widest text-gray-500 mt-1">Click any tab on the left to edit that section.</div>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="admin-settings-save">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* SIDEBAR: section tabs */}
        <aside className="bg-white border border-gray-200 self-start lg:sticky lg:top-4 max-h-[calc(100vh-7rem)] overflow-y-auto" data-testid="settings-sidebar">
          <div className="bg-[#1A1A1A] text-white px-4 py-3">
            <div className="font-heading font-black uppercase tracking-tight text-sm">Sections</div>
          </div>
          <nav className="py-2">
            {sectionNames.map((sec) => {
              const active = sec === current;
              return (
                <button
                  key={sec}
                  type="button"
                  onClick={() => setActiveSection(sec)}
                  className={`w-full text-left px-4 py-2.5 text-xs uppercase font-bold tracking-widest border-l-4 transition-colors ${
                    active ? "border-[#E65C00] bg-[#FFCC00]/15 text-[#1A1A1A]" : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-[#1A1A1A]"
                  }`}
                  data-testid={`settings-tab-${sec.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                >
                  {sec}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* MAIN: only the active section's fields */}
        <div className="bg-white border border-gray-200 min-w-0" data-testid={`settings-section-active`}>
          <div className="bg-[#1A1A1A] text-white px-5 py-3 flex items-center justify-between">
            <div className="font-heading font-black uppercase tracking-tight">{current}</div>
            <button onClick={save} disabled={saving} className="text-[10px] uppercase font-bold tracking-widest bg-[#E65C00] hover:bg-[#FFCC00] hover:text-[#1A1A1A] text-white px-3 py-1.5 transition-colors" data-testid="admin-settings-save-section">
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
          <div className="p-6 grid md:grid-cols-2 gap-5">
            {(sections[current] || []).map((f) => (
              <div key={f.name} className={f.type === "textarea" || f.type === "image" || f.type === "imagelist" || f.type === "stringlist" || f.type === "objectlist" || f.type === "videofile" || f.type === "boolean" || f.type === "file" ? "md:col-span-2" : ""}>
                {renderField(f)}
              </div>
            ))}
          </div>
          <div className="px-6 pb-6 flex justify-end">
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="admin-settings-save-bottom">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save All Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
